import { useCallback, useRef, useState } from "react";
import { useWidgetRequests } from "../context/WidgetRequestContext";

/**
 * Tracks a single in-flight async action and ignores re-entry until it settles.
 * Inside the widget the action also counts as work on the current screen, so the
 * screen's Next-style controls stay disabled until it settles.
 */
export function useInFlight(): {
  isPending: boolean;
  run: <T>(action: () => Promise<T>) => Promise<T | undefined>;
} {
  const pendingRef = useRef(false);
  const [isPending, setIsPending] = useState(false);
  const beginAction = useWidgetRequests()?.beginAction;

  const run = useCallback(
    async <T>(action: () => Promise<T>): Promise<T | undefined> => {
      if (pendingRef.current) return undefined;

      pendingRef.current = true;
      setIsPending(true);
      const endAction = beginAction?.();
      try {
        return await action();
      } finally {
        pendingRef.current = false;
        setIsPending(false);
        endAction?.();
      }
    },
    [beginAction],
  );

  return { isPending, run };
}
